'use client';
import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useBreakout } from './BreakoutContext';
import type { BreakoutRoom } from './BreakoutContext';
import { useParticipants, useLocalParticipant } from '@livekit/components-react';

// ---- Componente auxiliar: toast de ação ----
function BreakoutToast({ message, onClose }: { message: string; onClose: () => void }) {
  return (
    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[70] bg-green-600 text-white px-6 py-3 rounded-lg shadow-xl font-medium animate-in slide-in-from-top-4 fade-in">
      {message}
    </div>
  );
}

// ---- Componente auxiliar: dropdown "Mover para" ----
function MoveToSelect({
  currentRoomId,
  rooms,
  onMove,
  includeMainRoom = false,
}: {
  currentRoomId: string | null;
  rooms: BreakoutRoom[];
  onMove: (targetRoomId: string | null) => void;
  includeMainRoom?: boolean;
}) {
  const otherRooms = rooms.filter((r) => r.id !== currentRoomId);
  if (otherRooms.length === 0 && !includeMainRoom) return null;

  return (
    <select
      className="bg-zinc-700 text-white text-xs rounded px-1 py-0.5 cursor-pointer border border-zinc-600 max-w-[130px] flex-shrink-0"
      value=""
      onChange={(e) => {
        const val = e.target.value;
        if (val === '__main__') onMove(null);
        else if (val) onMove(val);
      }}
    >
      <option value="" disabled>
        Mover para...
      </option>
      {includeMainRoom && <option value="__main__">Sessão principal</option>}
      {otherRooms.map((r) => (
        <option key={r.id} value={r.id}>
          {r.name}
        </option>
      ))}
    </select>
  );
}

// ---- Tipos ----
type Tab = 'salas' | 'participantes' | 'auto';
type BreakoutControlPanelProps = { isOpen?: boolean; onOpenChange?: (open: boolean) => void };

// ---- Componente principal ----
export function BreakoutControlPanel(props: BreakoutControlPanelProps = {}) {
  const { isOpen: externalIsOpen, onOpenChange } = props;
  const {
    isHost, rooms, createRooms, addRoom, distributeAutomatically,
    assignParticipant, moveParticipant, startBreakout, initiateCloseBreakout,
    isInBreakout, closingTimer, isSessionActive, resetState,
  } = useBreakout();

  const participants = useParticipants();
  const { localParticipant } = useLocalParticipant();

  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('salas');
  const [groupSize, setGroupSize] = useState(5);
  const [roomCount, setRoomCount] = useState(2);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [participantSearch, setParticipantSearch] = useState('');

  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = (open: boolean) => {
    if (onOpenChange) onOpenChange(open);
    else setInternalIsOpen(open);
  };

  // Participantes elegíveis (todos exceto o host local)
  const eligibleParticipants = useMemo(
    () => participants.filter((p) => p.identity !== localParticipant.identity),
    [participants, localParticipant.identity],
  );
  const totalCount = eligibleParticipants.length;

  const unassignedParticipants = useMemo(
    () => eligibleParticipants.filter((p) => !rooms.some((r) => r.participants.includes(p.identity))),
    [eligibleParticipants, rooms],
  );

  const filteredParticipants = useMemo(() => {
    const q = participantSearch.toLowerCase();
    return eligibleParticipants.filter((p) =>
      (p.name || p.identity).toLowerCase().includes(q),
    );
  }, [eligibleParticipants, participantSearch]);

  const getRoomForParticipant = (identity: string) =>
    rooms.find((r) => r.participants.includes(identity));

  // Mover participante — usa moveParticipant (durante sessão) ou assignParticipant (antes)
  const handleMove = (identity: string, targetRoomId: string | null) => {
    if (isSessionActive) {
      moveParticipant(identity, targetRoomId);
    } else {
      if (targetRoomId === null) {
        assignParticipant(identity, '');
      } else {
        assignParticipant(identity, targetRoomId);
      }
    }
  };

  const handleStart = () => {
    setToastMessage('🚀 Iniciando salas de apoio...');
    startBreakout();
    setIsOpen(false);
  };

  const handleAutoDistribute = () => {
    const identities = eligibleParticipants.map((p) => p.identity);
    distributeAutomatically(groupSize, identities);
    setActiveTab('salas');
  };

  // Sem modal: mostra timer de encerramento ou toast de início
  if (!isHost || isInBreakout) return null;

  if (!isOpen) {
    if (closingTimer !== null) {
      return (
        <div className="fixed top-4 left-4 z-50">
          <div className="bg-red-600 text-white px-4 py-2 rounded shadow border border-red-500 text-sm font-bold flex items-center gap-2 animate-pulse">
            <span>⏳</span> Retornando em {closingTimer}s...
          </div>
        </div>
      );
    }
    return (
      <>
        {toastMessage && (
          <BreakoutToast message={toastMessage} onClose={() => setToastMessage(null)} />
        )}
      </>
    );
  }

  // Modal de encerramento (countdown)
  if (closingTimer !== null) {
    const closingContent = (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm">
        <div className="bg-[#1e1e1e] p-8 rounded-xl border border-red-500/50 text-center space-y-4 shadow-2xl">
          <div className="text-4xl animate-bounce">🏠</div>
          <h2 className="text-2xl font-bold text-white">Voltando para a sala principal</h2>
          <div className="text-5xl font-mono font-bold text-red-500">{closingTimer}s</div>
          <p className="text-gray-400 text-sm">Aguarde, movendo participantes...</p>
        </div>
      </div>
    );
    if (typeof window !== 'undefined') return createPortal(closingContent, document.body);
    return closingContent;
  }

  // ---- Conteúdo das abas ----

  const renderSalasTab = () => (
    <div className="space-y-3">
      {rooms.length === 0 && (
        <p className="text-center text-gray-500 py-4 text-sm">Nenhuma sala criada.</p>
      )}

      {rooms.map((room) => (
        <div key={room.id} className="border border-gray-600 bg-[#252525] rounded-lg p-3">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-sm font-bold text-blue-400">{room.name}</h3>
            <span className="text-xs text-gray-500 bg-black/30 px-2 py-0.5 rounded-full">
              {room.participants.length} {room.participants.length === 1 ? 'pessoa' : 'pessoas'}
            </span>
          </div>
          <div className="space-y-1">
            {room.participants.map((pid) => {
              const pName = participants.find((p) => p.identity === pid)?.name || pid;
              return (
                <div
                  key={pid}
                  className="flex justify-between items-center bg-zinc-800 px-2 py-1.5 rounded text-xs text-white gap-2"
                >
                  <span className="truncate flex-1">{pName}</span>
                  <MoveToSelect
                    currentRoomId={room.id}
                    rooms={rooms}
                    onMove={(targetRoomId) => handleMove(pid, targetRoomId)}
                    includeMainRoom={isSessionActive}
                  />
                </div>
              );
            })}
            {room.participants.length === 0 && (
              <p className="text-xs text-gray-600 italic px-1">Sala vazia</p>
            )}
          </div>
        </div>
      ))}

      {/* Participantes não atribuídos */}
      {unassignedParticipants.length > 0 && (
        <div className="border border-yellow-600/40 bg-yellow-900/10 rounded-lg p-3 mt-1">
          <h3 className="text-sm font-bold text-yellow-400 mb-2">
            Não atribuídos ({unassignedParticipants.length})
          </h3>
          <div className="space-y-1">
            {unassignedParticipants.map((p) => (
              <div
                key={p.identity}
                className="flex justify-between items-center bg-zinc-800/60 px-2 py-1.5 rounded text-xs text-white gap-2"
              >
                <span className="truncate flex-1">{p.name || p.identity}</span>
                {rooms.length > 0 && (
                  <select
                    className="bg-zinc-700 text-white text-xs rounded px-1 py-0.5 cursor-pointer border border-zinc-600 max-w-[130px]"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) handleMove(p.identity, e.target.value);
                    }}
                  >
                    <option value="" disabled>Mover para...</option>
                    {rooms.map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Criar salas (só antes da sessão, sem salas ainda) */}
      {!isSessionActive && rooms.length === 0 && (
        <div className="flex items-center gap-2 justify-center flex-wrap pt-2">
          <span className="text-gray-400 text-sm">Criar</span>
          <input
            type="number"
            min={1}
            max={50}
            value={roomCount}
            onChange={(e) => setRoomCount(Math.max(1, Math.min(50, Number(e.target.value))))}
            className="bg-black border border-gray-600 text-white rounded p-1 w-14 text-center text-sm"
          />
          <span className="text-gray-400 text-sm">sala{roomCount !== 1 ? 's' : ''}</span>
          <button
            data-testid="criar-salas-btn"
            onClick={() => createRooms(roomCount)}
            className="bg-zinc-700 hover:bg-zinc-600 px-3 py-1 rounded text-white text-sm transition-colors"
          >
            Criar
          </button>
        </div>
      )}

      {/* Adicionar sala individual (a qualquer momento) */}
      {rooms.length > 0 && (
        <button
          onClick={addRoom}
          className="w-full mt-1 border border-dashed border-gray-600 text-gray-400 hover:border-blue-400 hover:text-blue-400 text-xs py-2 rounded-lg transition-colors"
        >
          + Adicionar sala
        </button>
      )}
    </div>
  );

  const renderParticipantesTab = () => (
    <div className="space-y-2">
      <input
        type="text"
        placeholder="Pesquisar participantes..."
        value={participantSearch}
        onChange={(e) => setParticipantSearch(e.target.value)}
        className="w-full bg-zinc-800 border border-gray-600 text-white text-sm rounded px-3 py-1.5 placeholder-gray-500 focus:outline-none focus:border-blue-400"
      />
      <div className="space-y-1">
        {filteredParticipants.length === 0 && (
          <p className="text-center text-gray-500 py-4 text-sm">Nenhum participante encontrado.</p>
        )}
        {filteredParticipants.map((p) => {
          const currentRoom = getRoomForParticipant(p.identity);
          return (
            <div
              key={p.identity}
              className="flex items-center justify-between bg-zinc-800 px-2 py-1.5 rounded text-xs text-white gap-2"
            >
              <span className="truncate flex-1">{p.name || p.identity}</span>
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap ${
                  currentRoom
                    ? 'bg-blue-900/50 text-blue-300'
                    : 'bg-yellow-900/40 text-yellow-400'
                }`}
              >
                {currentRoom ? currentRoom.name : 'Não atribuído'}
              </span>
              {rooms.length > 0 && (
                <MoveToSelect
                  currentRoomId={currentRoom?.id ?? null}
                  rooms={rooms}
                  onMove={(targetRoomId) => handleMove(p.identity, targetRoomId)}
                  includeMainRoom={isSessionActive}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderAutoTab = () => {
    const numRooms = groupSize > 0 ? Math.ceil(totalCount / groupSize) : 0;
    const remainder = totalCount % groupSize;
    const fullRooms = remainder === 0 ? numRooms : numRooms - 1;

    return (
      <div className="flex flex-col items-center justify-center py-4 space-y-4">
        <p className="text-gray-300 text-sm">
          Dividir <span className="text-white font-bold">{totalCount}</span> participantes em grupos de:
        </p>

        {groupSize > 0 && totalCount > 0 && (
          <div className="text-center space-y-1 px-4">
            <p className="text-sm text-gray-400">
              Serão criadas{' '}
              <span className="text-blue-400 font-bold">{numRooms}</span>{' '}
              sala{numRooms !== 1 ? 's' : ''}
            </p>
            <p className="text-xs text-gray-500">
              {remainder === 0 ? (
                <span>
                  Com <span className="text-blue-400 font-semibold">{groupSize}</span> participantes por sala
                </span>
              ) : (
                <span>
                  {fullRooms > 0 && (
                    <span>
                      {fullRooms} sala{fullRooms !== 1 ? 's' : ''} com{' '}
                      <span className="text-blue-400 font-semibold">{groupSize}</span> participantes
                    </span>
                  )}
                  {fullRooms > 0 && remainder > 0 && ' e '}
                  {remainder > 0 && (
                    <span>
                      1 sala com{' '}
                      <span className="text-blue-400 font-semibold">{remainder}</span>{' '}
                      participante{remainder !== 1 ? 's' : ''}
                    </span>
                  )}
                </span>
              )}
            </p>
          </div>
        )}

        <input
          type="number"
          min="1"
          value={groupSize}
          onChange={(e) => setGroupSize(Number(e.target.value))}
          className="bg-black border border-gray-600 text-white rounded p-2 w-20 text-center"
        />

        <button
          onClick={handleAutoDistribute}
          className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded text-white font-bold transition-colors"
        >
          Distribuir
        </button>
      </div>
    );
  };

  // ---- Modal principal ----
  const modalContent = (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="bg-[#1e1e1e] w-[620px] max-h-[85vh] rounded-xl border border-gray-700 shadow-2xl flex flex-col animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
          <h2 className="text-white font-bold text-lg">Gerenciar Salas de Apoio</h2>
          <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white text-xl">
            ✕
          </button>
        </div>

        {/* Banner de sessão ativa */}
        {isSessionActive && (
          <div className="bg-blue-900/30 border-b border-blue-500/30 p-2 text-center text-xs text-blue-200 flex justify-center items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
            </span>
            Sessão em andamento — você pode mover participantes entre salas.
          </div>
        )}

        {/* Abas */}
        <div className="flex border-b border-gray-700">
          {(
            [
              { key: 'salas' as Tab, label: `Salas (${rooms.length})` },
              { key: 'participantes' as Tab, label: `Participantes (${totalCount})` },
              { key: 'auto' as Tab, label: 'Automático', disabled: isSessionActive },
            ]
          ).map(({ key, label, disabled }) => (
            <button
              key={key}
              onClick={() => { if (!disabled) setActiveTab(key); }}
              disabled={disabled}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === key
                  ? 'text-blue-400 border-b-2 border-blue-400 bg-[#252525]'
                  : 'text-gray-400 hover:text-gray-200'
              } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Conteúdo */}
        <div className="p-4 flex-1 overflow-y-auto custom-scrollbar">
          {activeTab === 'salas' && renderSalasTab()}
          {activeTab === 'participantes' && renderParticipantesTab()}
          {activeTab === 'auto' && !isSessionActive && renderAutoTab()}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 flex justify-between items-center bg-[#252525] rounded-b-xl">
          {!isSessionActive && rooms.length > 0 ? (
            <button onClick={resetState} className="text-red-400 text-sm hover:underline">
              Resetar
            </button>
          ) : (
            <div />
          )}

          <div className="flex gap-2">
            <button
              onClick={() => {
                initiateCloseBreakout();
                setIsOpen(false);
              }}
              disabled={!isSessionActive}
              className={`px-4 py-2 border font-bold rounded shadow transition-colors ${
                isSessionActive
                  ? 'border-red-600 text-red-500 hover:bg-red-600/10 cursor-pointer'
                  : 'border-zinc-700 text-zinc-600 cursor-not-allowed opacity-50'
              }`}
            >
              Encerrar Todas
            </button>

            <button
              onClick={handleStart}
              disabled={isSessionActive || rooms.length === 0}
              className={`px-6 py-2 font-bold rounded shadow flex items-center gap-2 transition-colors ${
                !isSessionActive && rooms.length > 0
                  ? 'bg-green-600 hover:bg-green-700 text-white cursor-pointer'
                  : 'bg-zinc-700 text-zinc-500 cursor-not-allowed opacity-50'
              }`}
            >
              <span>{isSessionActive ? 'Em Andamento' : '🚀 Iniciar Salas'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof window !== 'undefined') return createPortal(modalContent, document.body);
  return modalContent;
}

// ---- Badge de status para participantes dentro de salas de apoio ----
export function BreakoutStatusBadge() {
  const { isInBreakout, returnToMainRoom, rooms, assignedRoomId, closingTimer } = useBreakout();

  if (closingTimer !== null) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
        <div className="bg-[#1e1e1e] p-8 rounded-2xl border-2 border-red-500 shadow-2xl flex flex-col items-center gap-6 max-w-md w-full mx-4">
          <div className="flex items-center gap-3">
            <span className="text-4xl animate-bounce">🏠</span>
            <h2 className="text-2xl font-bold text-white">Voltando para a sala principal</h2>
          </div>

          <div className="flex flex-col items-center">
            <span className="text-xs uppercase text-gray-400 font-semibold tracking-widest mb-2">
              Redirecionando em
            </span>
            <div className="text-7xl font-mono font-bold text-white tabular-nums">
              {closingTimer}
              <span className="text-2xl text-gray-500">s</span>
            </div>
          </div>

          <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
            <div
              className="bg-red-500 h-full transition-all duration-1000 ease-linear"
              style={{ width: `${(closingTimer / 5) * 100}%` }}
            />
          </div>

          <button
            onClick={returnToMainRoom}
            className="w-full bg-white text-black hover:bg-gray-200 font-bold py-3 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            <span>🚀</span> Ir Agora
          </button>
        </div>
      </div>
    );
  }

  if (!isInBreakout) return null;

  const currentRoomName = rooms.find((r) => r.id === assignedRoomId)?.name || 'Sala de Apoio';

  return (
    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-blue-600/90 backdrop-blur-sm text-white pl-4 pr-1 py-1.5 rounded-full shadow-lg border border-blue-400/30 flex items-center gap-3 animate-in slide-in-from-top-4">
      <div className="flex flex-col items-start leading-none py-1">
        <span className="text-[10px] opacity-80 uppercase tracking-wider">Sala Atual</span>
        <span className="font-bold text-sm max-w-[150px] truncate">{currentRoomName}</span>
      </div>
      <div className="h-8 w-px bg-white/20 mx-1" />
      <button
        onClick={returnToMainRoom}
        className="bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-full text-xs font-bold transition-colors flex items-center gap-1"
      >
        <span>↪</span> Sair
      </button>
    </div>
  );
}
